import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { Button, Text } from '@lmn/design-system';
import styled, { css } from 'styled-components';
import { parse, format } from 'date-fns';
import { useTranslation } from '@/app/components/translations/Translation';
import useUserConfig from '@/app/components/hotel-detail/hooks/useUserConfig';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { PaymentType } from '@/app/components/hotel-detail/rooms/PaymentType';
import { LocalTaxes } from '@/app/components/hotel-detail/rooms/LocalTaxes';
import type { MealPlanTypeOption } from '@/app/components/hotel-detail/rooms/RoomPanelCommon';
import {
  getPriceDifferenceByCancellationType,
  getRoomBedTypesOptions,
  getRoomCancellationOptions,
  getRoomMealPlanTypeOptions,
  haveAllCancellationTypesAMealPlanType,
  MealPlanOption,
  MealPlanOptionGroup,
  MealPlanOptionGroupTitle,
  MealPlanOptionsGroups,
  StyledRadio,
} from '@/app/components/hotel-detail/rooms/RoomPanelCommon';
import {
  getCancellationBeforeDate,
  getCancellationDateformat,
  SHOPPING_API_DATE_FORMAT,
} from '@/app/components/hotel-detail/common/dates';
import { URLParameters } from '@/app/common/urlParameters';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { lastminuteDealsGradientOnButton } from '@/app/common/components/Promotions/styled';
import LastMinuteDealsBadge from '@/app/common/components/Promotions/LastMinuteDealsBadge';
import LoyaltyBadge from '@/app/common/components/LoyaltyBadge';
import { getTotalPerNightsLabel } from '@/app/components/search/components/Card/utils/getPriceDetails';
import { useCommonClient } from '@/app/common/commonClientProvider';
import getSearchParams from '@/app/components/search/common/getSearchParams';
import { createDetailPageUserActionPayload } from '@/app/common/starTrack/events/detailPageUserAction.ts';
import { PeriodicPromotion } from '@/app/common/components/Promotions/LastMinuteDealsBadge/PeriodicPromotion';
import { getActivePeriodicPromotion } from '@/app/common/periodicPromotions';
import { MealPlanDetails } from '@/app/components/hotel-detail/rooms/RoomMealPlanInformation';
import { RoomPanelPrice } from '@/app/components/hotel-detail/rooms/RoomPanelPrice';
import { getPromotionLabel } from '@/app/components/hotel-detail/rooms/promotionLabel';
import { groupPromotionsByLabel } from '@/app/components/hotel-detail/rooms/groupPromotionsByLabel';
import { keyUpHandler } from '@/app/components/hotel-detail/common/common.helper';
import { DebugRoomInfo } from '@/app/components/hotel-detail/DebugRoomInfo.tsx';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import { Icon } from '@/app/common/icons/Icon';
import { getOptionKey, hasFlashSalePromotion, isPriceOnlyPromotion } from './roomCommon';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { useIsMobile } from '../hooks/useMediaSSR';
import { RoomBedTypeSelection, shouldShowBedSelection } from './RoomBedTypeSelection';
import { MyPriceDiscountPercentage, RoomPrice } from './RoomPrice';
import { EmployeeDiscountBadge, MobileRatesBadge, MyPriceBadge } from './RoomBadges';
import { CancellationPoliciesModal } from './CancellationPolicyModal';
import { hasMealPlanIncludedOptions } from './mealPlanIncludedOptions';
import { MealPlanIncludedOptionsInfoButton } from './MealPlanIncludedOptionsInfoButton';
import { MealPlanIncludedOptionsModal } from './MealPlanIncludedOptionsModal';
import { createPriceByDisplayType, formatPrice } from '../../search/utils/formatting';

type MealPlanOptionsProps = {
  context: 'room-panel' | 'room-panel-modal';
  hideAdditionalInformation?: boolean;
  onContinueClick?: (selectedMealPlan: MealPlanType) => void;
  onSelectMealPlan?: (selectedMealPlan: MealPlanType) => void;
  room: RoomType;
  showBedSelection?: boolean;
  showSelectedMealPlanPriceSection?: boolean;
  selectedMealPlan: MealPlanType;
  variation: 'single-column' | 'two-columns';
} & React.HTMLAttributes<HTMLElement>;

export const MealPlanOptionsContainer = styled.div<{ $isTwoColumnsLayout: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  justify-content: space-between;
  row-gap: 16px;

  ${(p) =>
    p.$isTwoColumnsLayout
      ? `
    flex-direction: row;
    height: 100%;

    & > div:first-child {
      width: 60%;
      min-width: 0;
    }

    & > div:nth-child(3) {
      width: 40%;
    }`
      : ``}
`;

export const VerticalSeparator = styled.div`
  height: auto;
  margin: 0 24px;
  border-left: 1px solid ${(p) => p.theme.commons.colors.gray100};
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-grow: 1;
  flex-wrap: wrap;
  padding-top: 24px;
`;

const PriceBadgesAndDiscount = styled.div`
  display: flex;
  gap: 4px 8px;
  flex-direction: column;
  align-items: end;
  justify-content: flex-start;
  flex-grow: 1;
  flex-wrap: wrap-reverse;
`;

const PriceDifference = styled(Text).attrs({ variant: 'Title6' })<{ $disabled: boolean }>`
  white-space: nowrap;
  ${(p) => (p.$disabled ? `color: ${p.theme.commons.colors.gray200}` : '')}
`;

const StyledButton = styled(Button)<{ $isLmnDeal?: boolean }>`
  width: 100%;
  margin-top: 16px;
  position: relative;
  display: inline;
  ${(p) => p.$isLmnDeal && lastminuteDealsGradientOnButton};
`;

const OptionsList = styled.ul`
  min-width: 30%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;

  &:empty {
    display: none;
  }
`;

const OptionItem = styled.li`
  display: flex;
  align-items: flex-start;
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  gap: 8px;
  padding: 2px 0;

  img {
    margin-top: 2px;
  }
`;

export const Separator = styled.hr`
  margin: 16px 0;
  border: 0;
  border-top: 1px solid ${(p) => p.theme.commons.colors.gray100};
`;

export const AdditionalInformationListSeparator = styled(Separator)``;

const TotalPricePerNight = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: 4px;
`;

const MealPlanPromotionLabel = styled(Text).attrs({ variant: 'BodySmall', color: 'textPrimaryDefault' })``;

/*
 * Meal Plan Options VERSION 2:
 *   - First choice Meal Plan Type
 *   - Second choice Cancellation Type
 *
 * Variations:
 *   - Default: Prices differences are fixed, based on the cheapest price
 *        - Meal Plan Type price differences are fixed and shown: When all the meal plan type options are available in the cheapest cancellation type.
 *            Example:
 *               ROOM ONLY - Cancellation Type: REFUNDABLE - 8/01/24 - £68
 *               BED AND BREAKFAST - Cancellation Type: REFUNDABLE - 8/01/24 - £82
 *               ROOM ONLY - Cancellation Type: REFUNDABLE - 9/01/24 - £105
 *               BED AND BREAKFAST - Cancellation Type: REFUNDABLE - 9/01/24 - £127
 *
 *            In this case both meal plan options are available in the cheaper cancellation type REFUNDABLE - 8/01/24,
 *            therefore the price differences are shown.
 *
 *        - Meal Plan Type price differences are not shown: When not all the meal plan type options are available in the cheapest cancellation type.
 *
 *               ROOM ONLY - Cancellation Type: NON_REFUNDABLE - £145
 *               ROOM ONLY - Cancellation Type: REFUNDABLE - 18/11/23 - £153
 *               BED AND BREAKFAST - Cancellation Type: NON_REFUNDABLE - £161
 *               BED AND BREAKFAST - Cancellation Type: REFUNDABLE - 18/11/23 - £168
 *               HALF BOARD - Cancellation Type: REFUNDABLE - 22/11/23 - £185
 *
 *           In this case there are 3 different meal plan options and only 2 are available in the cheaper free cancellation NON_REFUNDABLE,
 *           therefore the price differences are not shown.
 *
 *           IMPORTANT!: BUT in the case the user select a meal plan type for a specific free cancellation type and exists a cheaper meal plan type for the
 *           same cancellation type, the price differences (negative) is shown in the cheaper meal plan type option.
 *           For example, if the user selects BED AND BREAKFAST and NON_REFUNDABLE (£161),
 *           in the ROOM ONLY option (£145) the price difference (-£16) will be shown.
 *
 *   - Dynamic Price Change: Prices differences are dynamic, based on the selected option.
 *
 */
export function MealPlanOptionsByMealPlan(props: MealPlanOptionsProps) {
  const {
    room,
    context,
    variation,
    hideAdditionalInformation = false,
    showBedSelection = false,
    showSelectedMealPlanPriceSection = false,
    onContinueClick: onContinueClickCallback,
    onSelectMealPlan,
    selectedMealPlan,
    ...rest
  } = props;
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const urlSearchParams = useUrlSearchParams();
  const { localization } = usePageWrapperData();
  const { showBackToTravel } = useUserConfig();
  const { starTrack } = useCommonClient();
  const initialParams = useInitialParams();
  const { productDetailKey } = initialParams;
  const searchParams = getSearchParams();
  const searchId = searchParams[URLParameters.SEARCH_ID];
  const { offersData } = useOffersContext();

  const [isCancellationPoliciesModalOpen, setCancellationPoliciesModalOpen] = useState<boolean | undefined>();
  const openCancellationPoliciesAndTrack = () => {
    starTrack?.detailPageUserAction(
      createDetailPageUserActionPayload({
        searchId: searchId || '',
        productDetailKey,
        action: 'CANCELLATION_POLICY',
      })
    );
    setCancellationPoliciesModalOpen(true);
  };

  // MPH-946: the info icon next to a meal plan name lists the meals/drinks that plan includes. Only
  // the rate id is kept: the modal body is looked up from the current configurations on every render,
  // so a forward navigation that reopens it after a rooms refetch cannot show a rate that is gone.
  const [mealPlanInfoRateId, setMealPlanInfoRateId] = useState<string | undefined>();
  const [isMealPlanInfoModalOpen, setMealPlanInfoModalOpen] = useState<boolean | undefined>();
  const openMealPlanInfoModal = (rateId: string) => {
    setMealPlanInfoRateId(rateId);
    setMealPlanInfoModalOpen(true);
  };

  const isTwoColumnsLayout = variation === 'two-columns';
  const dynamicPriceChange = isMobile;
  const hidePriceDifferences = !urlSearchParams.accommodationOnly && !isMobile;
  const mealPlans = room.configurations;
  const useStructuredTransfer = !!offersData?.transfer;
  const mealPlanInfo = mealPlanInfoRateId
    ? mealPlans.find((mealPlan) => mealPlan.id === mealPlanInfoRateId)
    : undefined;

  const cancellationPolicies = useMemo(() => getRoomCancellationOptions(mealPlans), [mealPlans]);
  const mealPlanTypes = useMemo(() => getRoomMealPlanTypeOptions(mealPlans), [mealPlans]);
  const bedTypes = useMemo(
    () => (showBedSelection ? getRoomBedTypesOptions(mealPlans, room.bedOptions) : []),
    [mealPlans, showBedSelection]
  );
  const extendedRoomPanel = localization?.configValuesApp?.mobileShowAllRooms || false;
  const showLastMinuteDeals = localization?.configValuesApp?.lastMinuteDeals === true;
  const isLmnDeal = showLastMinuteDeals && hasFlashSalePromotion(selectedMealPlan);

  const firstMealPlanTypeAvailableInAllCancellationTypes = useMemo(
    () =>
      haveAllCancellationTypesAMealPlanType(
        mealPlans,
        Object.values(cancellationPolicies),
        Object.values(mealPlanTypes)[0]
      ),
    [mealPlans]
  );

  const onChangeCancellationPolicy = (targetMealPlan: MealPlanType | undefined) => {
    if (targetMealPlan) {
      onSelectMealPlan?.(targetMealPlan);
    }
  };

  const onContinueClick = () => {
    onContinueClickCallback?.(selectedMealPlan);
  };

  const onChangeMealPlanType = (newMealPlanType: MealPlanTypeOption) => {
    const { boardClass, promotionKey } = newMealPlanType;
    const targetMealPlan = findMealPlan(
      mealPlans,
      boardClass,
      promotionKey,
      selectedMealPlan.bedId,
      selectedMealPlan.cancellationPolicy.type,
      selectedMealPlan.cancellationPolicy.upTo
    );

    if (targetMealPlan) {
      onSelectMealPlan?.(targetMealPlan);
    }
  };

  const mealPlansByMealType = mealPlans.filter(
    (mealPlan) =>
      mealPlan.boardClass === selectedMealPlan.boardClass && mealPlan.promotionKey === selectedMealPlan.promotionKey
  );

  const cancellationOptions = Object.values(cancellationPolicies).sort((a) => (a.type === 'NOT_REFUNDABLE' ? -1 : 1));
  const mealPlanOptions = Object.values(mealPlanTypes);
  const bedTypesOptions = Object.values(bedTypes);

  const showDeferredPayments = mealPlans.some((mealplan) => mealplan.deferredPaymentDeposit);

  // On desktop (two columns layout), deferred payments are shown in the additional info list (gray line)
  // On mobile (single column), deferred payments are shown in the cancellation section (pink line)
  const showAdditionalInfoList =
    (isTwoColumnsLayout && showDeferredPayments) ||
    (showBackToTravel && selectedMealPlan.backToTravel !== undefined) ||
    selectedMealPlan.paymentType !== 'PAY_NOW' ||
    selectedMealPlan.promotions !== undefined;

  return (
    <MealPlanOptionsContainer {...rest} $isTwoColumnsLayout={isTwoColumnsLayout}>
      <MealPlanOptionsGroups
        $hideBorder={isTwoColumnsLayout}
        css={`
          ${isTwoColumnsLayout ? 'flex: 1;' : undefined}
        `}
      >
        <MealPlanOptionGroupTitle
          css={`
            height: 28px;
          `}
        >
          {t('vacation.select.room_panel.meal_type')}
        </MealPlanOptionGroupTitle>
        {mealPlanOptions.length > 1 ? (
          <MealPlanOptionGroup>
            {mealPlanOptions.map((mealPlanType) => {
              const { boardClass, promotionKey, promotions } = mealPlanType;

              const promotionsToShow = getPromotionsToShow(promotions, useStructuredTransfer);

              const targetMealPlan = mealPlans.find(
                (mealPlan) =>
                  mealPlan.cancellationPolicy.type === selectedMealPlan.cancellationPolicy.type &&
                  mealPlan.cancellationPolicy.upTo === selectedMealPlan.cancellationPolicy.upTo &&
                  mealPlan.bedId === selectedMealPlan.bedId &&
                  mealPlan.boardClass === boardClass &&
                  mealPlan.promotionKey === promotionKey
              );

              const checked = selectedMealPlan.id === targetMealPlan?.id;
              const value = `${boardClass}${promotionKey || ''}`;
              // The included options describe what the guest actually gets, so they come from the rate
              // this option resolves to under the current cancellation type and bed — not from the
              // group's cheapest config, whose options can differ within one boardClass.
              const optionName = (
                <Text variant="BodySmall">
                  {t(getOptionKey(boardClass, urlSearchParams.accommodationOnly))}
                  {targetMealPlan && hasMealPlanIncludedOptions(targetMealPlan) ? (
                    <MealPlanIncludedOptionsInfoButton onClick={() => openMealPlanInfoModal(targetMealPlan.id)} />
                  ) : null}
                </Text>
              );

              const priceDifference = getPriceDifferenceByMealPlanType(mealPlans, mealPlanType, localization);

              const priceDifferenceWithSelected = dynamicPriceChange
                ? getPriceDifferenceForMealPlanType(mealPlans, selectedMealPlan, mealPlanType, localization)
                : 0;

              const priceDifferenceText = dynamicPriceChange
                ? checked || !priceDifferenceWithSelected
                  ? ''
                  : `${priceDifferenceWithSelected > 0 ? '-' : '+'} ${formatPrice({ price: Math.abs(priceDifferenceWithSelected), localization })}`
                : priceDifference
                  ? `+ ${formatPrice({ price: priceDifference, localization })}`
                  : '';

              return (
                <MealPlanOption
                  key={`${context}-mealPlanType-${value}`}
                  tabIndex={0}
                  role="radio"
                  aria-checked={checked}
                  onClick={() => onChangeMealPlanType(mealPlanType)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      const currentTarget = e.currentTarget as HTMLElement;
                      onChangeMealPlanType(mealPlanType);
                      // Maintain focus after selection using requestAnimationFrame
                      requestAnimationFrame(() => {
                        currentTarget.focus();
                      });
                    }
                  }}
                >
                  <StyledRadio
                    $alignTop={!!promotionsToShow?.length}
                    label={
                      promotionsToShow?.length ? (
                        <div
                          css={`
                            display: flex;
                            row-gap: 4px;
                            flex-direction: column;
                          `}
                        >
                          {optionName}
                          {groupPromotionsByLabel(promotionsToShow).map((promotionGroup) => (
                            <MealPlanPromotionLabel key={promotionGroup.label}>
                              {getPromotionLabel(promotionGroup, t)}
                            </MealPlanPromotionLabel>
                          ))}
                        </div>
                      ) : (
                        optionName
                      )
                    }
                    name={`${context}-mealPlanType-${room.roomId}-${room.roomsIncludedInPrice}`}
                    value={value}
                    readOnly={true}
                    data-testid={`room-option-${value}`}
                    hoverBackground={false}
                    checked={checked}
                    tabIndex={-1}
                  />
                  {!hidePriceDifferences && priceDifferenceText ? (
                    <PriceDifference $disabled={false}>{priceDifferenceText}</PriceDifference>
                  ) : undefined}
                </MealPlanOption>
              );
            })}
          </MealPlanOptionGroup>
        ) : (
          <SingleOption icon="check" alignTop={true}>
            <div
              css={`
                display: flex;
                flex-direction: column;
                row-gap: 4px;
              `}
            >
              <Text variant="BodySmall">
                {t(getOptionKey(selectedMealPlan.boardClass, urlSearchParams.accommodationOnly))}
                {hasMealPlanIncludedOptions(selectedMealPlan) ? (
                  <MealPlanIncludedOptionsInfoButton onClick={() => openMealPlanInfoModal(selectedMealPlan.id)} />
                ) : null}
              </Text>
              {groupPromotionsByLabel(getPromotionsToShow(selectedMealPlan.promotions, useStructuredTransfer)).map(
                (promotionGroup) => (
                  <MealPlanPromotionLabel key={promotionGroup.label}>
                    {getPromotionLabel(promotionGroup, t)}
                  </MealPlanPromotionLabel>
                )
              )}
            </div>
          </SingleOption>
        )}
        <Separator />
        <div
          role="button"
          tabIndex={0}
          onClick={() => openCancellationPoliciesAndTrack()}
          onKeyDown={(event) => (event.key === 'Enter' ? openCancellationPoliciesAndTrack() : null)}
          css={`
            cursor: pointer;
          `}
        >
          <MealPlanOptionGroupTitle
            css={`
              display: flex;
              align-items: center;
              justify-content: space-between;
            `}
          >
            {t('vacation.select.room_panel.cancellation')}
            <Icon
              category="Semantics"
              name="icon-circle-info"
              size={20}
              color="iconSecondary"
              alt="Cancellation policy info"
            />
          </MealPlanOptionGroupTitle>
        </div>
        {cancellationOptions.length > 1 ? (
          <MealPlanOptionGroup>
            {cancellationOptions.map(({ type, upTo, deferredPaymentDepositPerMealPlan }) => {
              const targetMealPlan = mealPlans.find(
                (mealPlan) =>
                  selectedMealPlan.boardClass === mealPlan.boardClass &&
                  mealPlan.cancellationPolicy.type === type &&
                  mealPlan.cancellationPolicy.upTo === upTo &&
                  mealPlan.bedId === selectedMealPlan.bedId
              );

              const disabled = targetMealPlan === undefined;
              const checked =
                selectedMealPlan.cancellationPolicy.type === type && selectedMealPlan.cancellationPolicy.upTo === upTo;

              const priceDifference = targetMealPlan ? selectedMealPlan.finalPrice - targetMealPlan.finalPrice : 0;

              const priceDifferenceByCancellationType =
                dynamicPriceChange && targetMealPlan
                  ? 0
                  : getPriceDifferenceByCancellationType(mealPlansByMealType, type, upTo, localization);

              // When all the cancellation options are available in the first (cheaper) mealplan type, we show the
              // free cancellation options with the price difference.
              const priceDifferenceText =
                (dynamicPriceChange && checked) || !firstMealPlanTypeAvailableInAllCancellationTypes
                  ? ''
                  : dynamicPriceChange && targetMealPlan
                    ? `${priceDifference > 0 ? '-' : '+'} ${formatPrice({
                        price: Math.abs(priceDifference),
                        localization,
                      })}`
                    : priceDifferenceByCancellationType === 0
                      ? ''
                      : `+ ${formatPrice({
                          price: priceDifferenceByCancellationType,
                          localization,
                        })}`;

              const deferredPaymentDepositInfo = deferredPaymentDepositPerMealPlan.find(
                (mealPlan) => mealPlan.boardClass === selectedMealPlan.boardClass
              );

              return (
                <MealPlanOption
                  key={`${context}-${type}-${upTo}`}
                  tabIndex={disabled ? -1 : 0}
                  role="radio"
                  aria-checked={checked}
                  aria-disabled={disabled}
                  onClick={() => !disabled && onChangeCancellationPolicy(targetMealPlan)}
                  onKeyDown={(e) => {
                    if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      e.stopPropagation();
                      const currentTarget = e.currentTarget as HTMLElement;
                      onChangeCancellationPolicy(targetMealPlan);
                      // Maintain focus after selection using requestAnimationFrame
                      requestAnimationFrame(() => {
                        currentTarget.focus();
                      });
                    }
                  }}
                >
                  <StyledRadio
                    label={
                      <MealPlanOptionCancellationLabel
                        type={type}
                        upTo={upTo}
                        deferredPaymentDeposit={
                          showDeferredPayments ? deferredPaymentDepositInfo?.deferredPaymentDeposit : undefined
                        }
                        // deferredPaymentDepositTotal={
                        //   showDeferredPayments ? deferredPaymentDepositInfo?.deferredPaymentDepositTotal : undefined
                        // }
                      />
                    }
                    name={`${context}-cancellation-${room.roomId}-${room.roomsIncludedInPrice}`}
                    value={`${type}-${upTo}`}
                    readOnly={true}
                    data-testid={`room-option-${type}`}
                    hoverBackground={false}
                    checked={checked}
                    disabled={disabled}
                    tabIndex={-1}
                  />
                  {!hidePriceDifferences && !disabled ? (
                    <PriceDifference $disabled={disabled}>{priceDifferenceText}</PriceDifference>
                  ) : null}
                </MealPlanOption>
              );
            })}
          </MealPlanOptionGroup>
        ) : (
          <SingleOption
            icon={selectedMealPlan.cancellationPolicy.type === 'NOT_REFUNDABLE' ? 'not_refundable' : 'refundable'}
          >
            <MealPlanOptionCancellationLabel
              type={selectedMealPlan.cancellationPolicy.type}
              upTo={selectedMealPlan.cancellationPolicy.upTo}
              deferredPaymentDeposit={
                !isTwoColumnsLayout && showDeferredPayments ? selectedMealPlan.deferredPaymentDeposit : undefined
              }
              // deferredPaymentDepositTotal={undefined}
            />
          </SingleOption>
        )}
        {bedTypesOptions?.length && shouldShowBedSelection(selectedMealPlan.bedId, room.bedOptions) ? (
          <>
            <Separator />
            <RoomBedTypeSelection
              context={context}
              room={room}
              selectedMealPlan={selectedMealPlan}
              onSelectMealPlan={onSelectMealPlan}
            />
          </>
        ) : null}
        {showSelectedMealPlanPriceSection && (
          <>
            <RoomPanelPrice
              hideTaxes={
                !!(
                  localization?.configValuesApp?.taxesIncludedPopup ||
                  localization?.configValuesApp?.taxesIncludedDoublePricing
                )
              }
              hideLoyaltyBadge={!urlSearchParams.accommodationOnly}
              mealPlan={selectedMealPlan}
              roomsIncludedInPrice={room.roomsIncludedInPrice}
              showPriceDifference={false}
              roomId={room.roomId}
              css={`
                justify-content: flex-end;
                flex-grow: 0;
                text-align: right;
                margin-top: 12px;
              `}
            />
            <StyledButton
              variant="primary"
              data-testid="continue-button"
              $isLmnDeal={isLmnDeal}
              onClick={() => onContinueClick()}
              onKeyUp={keyUpHandler(() => onContinueClick())}
            >
              {urlSearchParams.accommodationOnly && extendedRoomPanel
                ? t('vacation.select.details.book.b')
                : t('vacation.select.details.book')}
            </StyledButton>
          </>
        )}
      </MealPlanOptionsGroups>
      {isTwoColumnsLayout ? (
        <TwoColumnsLayoutPriceSection
          selectedMealPlan={selectedMealPlan}
          roomId={room.roomId}
          roomsIncludedInPrice={room.roomsIncludedInPrice}
          showAdditionalInfoList={showAdditionalInfoList}
          onContinueClick={onContinueClick}
          showContinueButton={true}
        />
      ) : !hideAdditionalInformation ? (
        <>
          <AdditionalInformationListSeparator />
          <AdditionalInformationList selectedMealPlan={selectedMealPlan} hideDeferredPayments={true} />
        </>
      ) : undefined}
      <CancellationPoliciesModal
        id={room.roomId}
        isOpen={isCancellationPoliciesModalOpen}
        mealPlans={mealPlans}
        onDismiss={() => setCancellationPoliciesModalOpen(false)}
        onOpenByBrowserHistory={() => openCancellationPoliciesAndTrack()}
      />
      {mealPlanInfo ? (
        <MealPlanIncludedOptionsModal
          id={`${context}-${room.roomId}`}
          mealPlan={mealPlanInfo}
          isOpen={isMealPlanInfoModalOpen}
          onDismiss={() => setMealPlanInfoModalOpen(false)}
          onOpenByBrowserHistory={() => openMealPlanInfoModal(mealPlanInfo.id)}
        />
      ) : null}
    </MealPlanOptionsContainer>
  );
}

type TwoColumnsLayoutPriceSectionProps = {
  selectedMealPlan: MealPlanType;
  onContinueClick: () => void;
  roomsIncludedInPrice: number;
  roomId: string;
  showAdditionalInfoList: boolean;
  showContinueButton: boolean;
};

function TwoColumnsLayoutPriceSection(props: TwoColumnsLayoutPriceSectionProps) {
  const {
    selectedMealPlan,
    onContinueClick,
    roomsIncludedInPrice,
    roomId,
    showAdditionalInfoList,
    showContinueButton,
  } = props;

  const { t } = useTranslation();
  const { offersData } = useOffersContext();
  const { isDoublePrice, isDoublePriceDP } = useUserConfig();
  const { accommodationOnly, totalGuestsWithoutInfants } = useUrlSearchParams();
  const { commonData, localization } = usePageWrapperData();

  const nights = offersData?.nights;
  const hasFlashSalePromo = hasFlashSalePromotion(selectedMealPlan);
  const showLastMinuteDeals = localization?.configValuesApp?.lastMinuteDeals === true;
  const isLmnDeal = hasFlashSalePromo && showLastMinuteDeals;
  // When a promotion period (Summer Sale, Black Friday, …) is active it takes over the badge, so we never
  // render both the LMN deal and the periodic promotion badge at once — mirroring the EVO room card.
  const activePeriodicPromotion = getActivePeriodicPromotion(commonData.serverTime, localization);
  const showPeriodicPromotion = hasFlashSalePromo && !!activePeriodicPromotion;
  const isLoyalty = selectedMealPlan.ratePriceType === 'LOYALTY';

  return (
    <div
      css={`
        width: 280px;
        margin: -24px -24px -24px 24px;
        padding: 24px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        box-shadow:
          0px 0px 4px 0px #00000005,
          0px 0px 10px 0px #0000000a,
          0px 0px 20px 0px #0000000a;
      `}
    >
      <PriceContainer
        css={`
          padding: 0;
          align-self: flex-end;
          text-align: right;

          ${MyPriceDiscountPercentage} {
            margin-bottom: 8px;
          }
        `}
      >
        <PriceBadgesAndDiscount>
          {selectedMealPlan.mobileRate && commonData.isMobileDevice ? (
            <div
              css={css`
                display: flex;
                align-items: flex-start;
              `}
            >
              <MobileRatesBadge />
            </div>
          ) : undefined}
          {selectedMealPlan.isEmployeeDiscount && <EmployeeDiscountBadge />}
          {isLmnDeal && !showPeriodicPromotion && <LastMinuteDealsBadge />}
          {selectedMealPlan.isMyPrice && <MyPriceBadge />}
          {showPeriodicPromotion && <PeriodicPromotion />}
        </PriceBadgesAndDiscount>

        {selectedMealPlan.isMyPrice && (
          <MyPriceDiscountPercentage
            discountPercentage={selectedMealPlan.discountPercentage}
            elementCss={'margin-bottom: 4px;'}
          />
        )}
        {accommodationOnly && isLoyalty && selectedMealPlan.discount ? (
          <LoyaltyBadge label={selectedMealPlan.discount} />
        ) : undefined}
        <RoomPrice
          hasFlashSalePromo={hasFlashSalePromo}
          mealPlan={selectedMealPlan}
          roomsIncludedInPrice={roomsIncludedInPrice}
          showPriceDifference={true}
        />
        {accommodationOnly && (
          <TotalPricePerNight>
            <Text variant="Caption" weight="bold">
              {getTotalPerNightsLabel({ nights, t })}
            </Text>
          </TotalPricePerNight>
        )}
        {accommodationOnly || !offersData?.sameExcludedChargesInAllTheOffers ? (
          <LocalTaxes
            css={`
              &:empty {
                display: none;
              }

              ${
                isDoublePrice || isDoublePriceDP
                  ? `
            flex-direction: column;
            `
                  : undefined
              }
            `}
            nights={nights}
            roomsIncludedInPrice={roomsIncludedInPrice}
            excludedCharges={selectedMealPlan.excludedCharges}
            excludedChargesIncluded={selectedMealPlan.excludedChargesIncluded}
            passengers={totalGuestsWithoutInfants}
            price={selectedMealPlan.finalTotalPrice}
            priceWithoutTaxes={selectedMealPlan.priceWithoutTaxes}
            showDoublePrice={accommodationOnly}
            showTotalPriceIncludedTaxes={false}
          />
        ) : undefined}
        <DebugRoomInfo mealPlan={selectedMealPlan} roomId={roomId} />
      </PriceContainer>
      {showAdditionalInfoList ? (
        <>
          <Separator
            css={`
              width: 100%;

              /* Hide separator when followed by empty list */
              &:has(+ [data-testid='mealplan-addedinfo-container']:empty) {
                display: none;
              }
            `}
          />
          <AdditionalInformationList
            hideAtol={true}
            selectedMealPlan={selectedMealPlan}
            css={`
              width: 100%;
            `}
          />
        </>
      ) : undefined}

      {showContinueButton && (
        <StyledButton
          variant="primary"
          size="large"
          data-testid="continue-button"
          onClick={onContinueClick}
          $isLmnDeal={isLmnDeal}
        >
          {accommodationOnly ? t('vacation.select.details.book.b') : t('vacation.select.details.book')}
        </StyledButton>
      )}
    </div>
  );
}

function AdditionalInformationList(
  props: {
    selectedMealPlan: MealPlanType;
    hideAtol?: boolean;
    hideDeferredPayments?: boolean;
  } & React.HTMLAttributes<HTMLElement>
) {
  const { selectedMealPlan, hideAtol = false, hideDeferredPayments = false, ...rest } = props;
  const { t } = useTranslation();
  const { offersData } = useOffersContext();
  const useStructuredTransfer = !!offersData?.transfer;

  return (
    <OptionsList data-testid="mealplan-addedinfo-container" {...rest}>
      <MealPlanDetails mealPlan={selectedMealPlan} hideAtol={hideAtol} hideDeferredPayments={hideDeferredPayments} />

      {selectedMealPlan.paymentType !== 'PAY_NOW' && (
        <OptionItem>
          <Icon category="Semantics" name="icon-check" size={16} color="iconPrimary" alt="check" />
          <PaymentType paymentType={selectedMealPlan.paymentType} />
        </OptionItem>
      )}
      {useStructuredTransfer && offersData?.transfer?.inbound.key && (
        <OptionItem
          css={`
            width: 100%;
          `}
        >
          <Icon category="Semantics" name="icon-check" size={16} color="iconPrimary" alt="check" />
          <Text variant="BodySmall">{t(offersData.transfer.inbound.key)}</Text>
        </OptionItem>
      )}
      {selectedMealPlan.promotions
        ?.filter(
          (promotion) =>
            promotion.type !== 'FLASH_SALE' && !(useStructuredTransfer && promotion.type === 'TRANSFER_INCLUDED')
        )
        // Suppliers order promotions differently per rate — alphabetical order keeps the list stable
        // when switching meal plans, so equal offers don't jump around (MPH-719).
        .sort((a, b) => (a.description || a.label).localeCompare(b.description || b.label))
        .map((promotion) => (
          <OptionItem
            // The same type+label can arrive with several descriptions (MPH-719) — the description
            // keeps the key unique, otherwise React's reconciliation duplicates rows on rate changes.
            key={`${promotion.type}-${promotion.label}-${promotion.description}`}
            css={`
              width: 100%;
            `}
          >
            <Icon category="Semantics" name="icon-check" size={16} color="iconPrimary" alt="check" />
            <Text variant="BodySmall">{promotion.description || getPromotionLabel(promotion, t)}</Text>
          </OptionItem>
        ))}
    </OptionsList>
  );
}

function MealPlanOptionCancellationLabel({
  type,
  upTo,
  deferredPaymentDeposit,
  // deferredPaymentDepositTotal,
}: {
  type: CancellationType;
  upTo?: string;
  deferredPaymentDeposit: number | undefined;
  // deferredPaymentDepositTotal: number | undefined;
}) {
  const { t } = useTranslation();
  const { accommodationOnly } = useUrlSearchParams();
  // const { isDoublePriceDP } = useUserConfig();
  const { localization } = usePageWrapperData();

  const showDeferredPayments = !!deferredPaymentDeposit;
  const showOneEuroDeferredPayment =
    showDeferredPayments && deferredPaymentDeposit === 1 && ['GBP', 'EUR'].includes(localization.currency);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <Text
        variant="BodySmall"
        css={css`
          padding: 0;
        `}
      >
        {`${t(getOptionKey(type, accommodationOnly))}${
          type === 'REFUNDABLE' && upTo
            ? ` ${t('vacation.common.mealplan.room.cancellationtype.refundable.before', {
                date: format(
                  parse(getCancellationBeforeDate(upTo), SHOPPING_API_DATE_FORMAT, new Date()),
                  getCancellationDateformat(localization.locale)
                ),
              })}`
            : ''
        }`}
      </Text>
      <Text variant="BodySmall" color="textSecondary">
        {showOneEuroDeferredPayment
          ? t('vacation.select.details.deferredpayments_deposit.min_deposit.room.option', {
              amount: formatPrice({ price: deferredPaymentDeposit, localization }),
            })
          : showDeferredPayments
            ? accommodationOnly
              ? t('vacation.select.details.deferredpayments_deposit_HO.room.option', {
                  amount: formatPrice({ price: deferredPaymentDeposit, localization }),
                })
              : // : isDoublePriceDP && deferredPaymentDepositTotal
                //   ? t('vacation.select.details.deferredpayments_deposit_total.room.option', {
                //       amount: formatPrice(
                //         deferredPaymentDepositTotal,
                //         config.localization.locale,
                //         config.localization.currency
                //       ),
                //     })
                t('vacation.select.details.deferredpayments_deposit.room.option', {
                  amount: formatPrice({ price: deferredPaymentDeposit, localization }),
                })
            : null}
      </Text>
    </div>
  );
}

function SingleOption({
  icon,
  children,
  alignTop = false,
}: {
  children: ReactNode;
  icon: 'check' | 'refundable' | 'not_refundable';
  // When the content wraps to several lines (e.g. a meal plan followed by its promotions), align the
  // leading icon with the first line instead of centring it across the whole block.
  alignTop?: boolean;
}) {
  return (
    <div
      css={`
        display: flex;
        align-items: ${alignTop ? 'flex-start' : 'center'};
        padding-top: 8px;
        gap: 8px;
      `}
    >
      <div
        css={`
          display: flex;
          align-items: center;
          height: 20px;
        `}
      >
        {icon === 'not_refundable' ? (
          <Icon
            category="Security and safety"
            name="icon-slashed-shield"
            size={20}
            color="iconPrimary"
            alt="non refundable"
          />
        ) : icon === 'refundable' ? (
          <Icon category="Security and safety" name="icon-shield" size={20} color="iconPrimary" alt="refundable" />
        ) : (
          <Icon category="Semantics" name="icon-check" size={20} color="iconPrimary" alt="check" />
        )}
      </div>
      {children}
    </div>
  );
}

function getPriceDifferenceByMealPlanType(
  allMealPlans: readonly MealPlanType[],
  mealPlanType: MealPlanTypeOption,
  localization: Localization
) {
  const {
    configValuesApp: { priceDisplayType },
  } = localization;
  const cheapestPrice = allMealPlans[0].finalPrice;
  const targetMealPlan = allMealPlans.find(
    (mealPlan) => mealPlan.boardClass === mealPlanType.boardClass && mealPlan.promotionKey === mealPlanType.promotionKey
  );

  if (!targetMealPlan?.finalPrice) return 0;

  // Calculate the difference in the same way as the display format
  const finalPriceVal = createPriceByDisplayType(targetMealPlan.finalPrice, priceDisplayType);
  const cheapestPriceVal = createPriceByDisplayType(cheapestPrice, priceDisplayType);
  return finalPriceVal - cheapestPriceVal;
}

function getPriceDifferenceForMealPlanType(
  allMealPlans: readonly MealPlanType[],
  selectedMealPlan: MealPlanType,
  mealPlanType: MealPlanTypeOption,
  localization: Localization
) {
  const {
    configValuesApp: { priceDisplayType },
  } = localization;
  let targetMealPlan = allMealPlans.find(
    (mealPlan) =>
      mealPlan.boardClass === mealPlanType.boardClass &&
      mealPlan.promotionKey === mealPlanType.promotionKey &&
      mealPlan.cancellationPolicy.type === selectedMealPlan.cancellationPolicy.type &&
      mealPlan.cancellationPolicy.upTo === selectedMealPlan.cancellationPolicy.upTo
  );

  if (!targetMealPlan) {
    targetMealPlan = allMealPlans.find(
      (mealPlan) =>
        mealPlan.boardClass === mealPlanType.boardClass && mealPlan.promotionKey === mealPlanType.promotionKey
    );
  }
  // Calculate the difference in the same way as the display format
  const selectedFinalPriceVal = createPriceByDisplayType(selectedMealPlan.finalPrice, priceDisplayType);
  const targetFinalPriceVal = createPriceByDisplayType(targetMealPlan?.finalPrice || 0, priceDisplayType);
  return targetMealPlan ? selectedFinalPriceVal - targetFinalPriceVal : 0;
}

function findMealPlan(
  mealPlans: readonly MealPlanType[],
  mealPlanType: BoardClassType,
  promotionKey: string | undefined,
  bedId: string | undefined,
  cancellationPolicy: CancellationType,
  upTo: string | undefined
) {
  let targetMealPlan = mealPlans.find(
    (mealPlan) =>
      mealPlan.boardClass === mealPlanType &&
      mealPlan.promotionKey === promotionKey &&
      mealPlan.cancellationPolicy.type === cancellationPolicy &&
      mealPlan.cancellationPolicy.upTo === upTo &&
      mealPlan.bedId === bedId
  );

  if (!targetMealPlan) {
    targetMealPlan = mealPlans.find(
      (mealPlan) =>
        mealPlan.boardClass === mealPlanType && mealPlan.promotionKey === promotionKey && mealPlan.bedId === bedId
    );
  }

  if (!targetMealPlan) {
    targetMealPlan = mealPlans.find(
      (mealPlan) => mealPlan.boardClass === mealPlanType && mealPlan.promotionKey === promotionKey
    );
  }

  return targetMealPlan;
}

function getPromotionsToShow(promotions: MealPlanPromotion[] | undefined, showPreattachTransfer: boolean) {
  return promotions?.filter((promotion) => {
    const isPreattachTransfer = showPreattachTransfer && promotion.type?.toUpperCase() === 'TRANSFER_INCLUDED';

    return !(isPriceOnlyPromotion(promotion) || isPreattachTransfer);
  });
}
